import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Agent } from "@/types";
import type { CreateAgentInput } from "@/api/schemas/agent";

// Mutation-Hooks für Agent-CRUD. Pattern wie M1-Queries: apiClient on-call
// instanziieren damit Auth-Session-Hydration nicht verpasst wird. Jeder Hook
// invalidiert ["resources","agents"] → list + alle agent-detail-queries
// refresht atomisch (hierarchisches QueryKey-Schema aus M1).

export interface CreateAgentResponse {
  agent: Agent;
  token: string;
  // #281: nur bei Profil mit Blaupause — Zähler + Warnungen (nicht gesetzte Einträge).
  profileApplied?: { plugins: number; connectors: number; warnings: string[] };
}

function useAgentMutationClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

export function useCreateAgent(): UseMutationResult<CreateAgentResponse, Error, CreateAgentInput> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    mutationFn: (input) => getClient().post<CreateAgentResponse>("/agents/create", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
    },
  });
}

export function useRevokeAgent(): UseMutationResult<Agent, Error, string> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    mutationFn: (id) => getClient().post<Agent>(`/agents/${id}/revoke`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
    },
  });
}

export function useDeleteAgent(): UseMutationResult<
  void,
  Error,
  { id: string; archive?: boolean; keepRepo?: boolean }
> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    // #199: ?archive=true sichert das claude-home-Volume vorher als tar.gz.
    // keepRepo=true → Gitea-Workspace-Repo wegrotieren statt löschen (Archiv behalten).
    mutationFn: ({ id, archive, keepRepo }) => {
      const params = new URLSearchParams();
      if (archive) params.set("archive", "true");
      if (keepRepo) params.set("keepRepo", "true");
      const qs = params.toString();
      return getClient().delete<void>(`/agents/${id}${qs ? `?${qs}` : ""}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
    },
  });
}

export function useSetAssignment(): UseMutationResult<
  Agent,
  Error,
  { id: string; nodeId: string | null }
> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    mutationFn: ({ id, nodeId }) =>
      getClient().put<Agent>(`/agents/${id}/assignment`, { nodeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
      // Assignment ändert die Node-Auslastung: Node-Detail "Assigned Agents"
      // + runningAgents/Drain-Counter (useNodeAgents/useNodes) müssen refetchen.
      qc.invalidateQueries({ queryKey: queryKeys.resource("nodes") });
    },
  });
}

export function useSetBrainSpec(): UseMutationResult<
  Agent,
  Error,
  { id: string; imageId: string | null }
> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    mutationFn: ({ id, imageId }) =>
      getClient().patch<Agent>(`/agents/${id}/brainSpec`, { imageId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
    },
  });
}

// #201: Explizite Lifecycle-Aktionen (Stop/Start/Restart/Suspend/Resume).
// Jede POSTet auf /agents/:id/<action> und invalidiert agents + nodes
// (runningAgents-Counter ändert sich). Eine Factory, fünf Hooks — DRY.
function useLifecycleAction(
  action: "start" | "stop" | "restart" | "suspend" | "resume",
): UseMutationResult<Agent, Error, string> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    mutationFn: (id) => getClient().post<Agent>(`/agents/${id}/${action}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
      qc.invalidateQueries({ queryKey: queryKeys.resource("nodes") });
    },
  });
}

export const useStartAgent = (): UseMutationResult<Agent, Error, string> =>
  useLifecycleAction("start");
export const useStopAgent = (): UseMutationResult<Agent, Error, string> =>
  useLifecycleAction("stop");
export const useRestartAgent = (): UseMutationResult<Agent, Error, string> =>
  useLifecycleAction("restart");
export const useSuspendAgent = (): UseMutationResult<Agent, Error, string> =>
  useLifecycleAction("suspend");
export const useResumeAgent = (): UseMutationResult<Agent, Error, string> =>
  useLifecycleAction("resume");

// #214/#215 (L2 / E3): Workspace neu aufbauen — Owner-Content verwerfen, Volume leeren,
// recreate falls running. Ohne seed → Reset aus der gespeicherten Quelle; mit seed
// {seedRepo, seedBranch} → Flash aus DIESEM Gitea-Repo@Branch (Repo gewinnt bei Konflikt).
export function useResetWorkspace(): UseMutationResult<
  Agent,
  Error,
  { id: string; seed?: { seedRepo: string; seedBranch: string | null } }
> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    mutationFn: ({ id, seed }) => getClient().post<Agent>(`/agents/${id}/workspace/reset`, seed),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
      qc.invalidateQueries({ queryKey: queryKeys.resource("nodes") });
    },
  });
}

// #214/#215 (L3): Gitea-Identität/Token rotieren (verloren/kompromittiert).
export function useRotateGiteaIdentity(): UseMutationResult<Agent, Error, string> {
  const qc = useQueryClient();
  const getClient = useAgentMutationClient();
  return useMutation({
    mutationFn: (id) => getClient().post<Agent>(`/agents/${id}/gitea/rotate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
    },
  });
}
